if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.submitHandler = this.submitHandler || this.handleSubmit.bind(this);
          this.addEventListener('submit', this.submitHandler);
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      // Variant rendering replaces the button while the product-form itself stays connected.
      const submitBtn = this.querySelector('[name="add"]');
      if (!submitBtn || submitBtn.disabled || this.submitting
        || submitBtn.getAttribute('aria-disabled') === 'true') return;
      this.submitBtn = submitBtn;
      this.submitting = true;
      clearTimeout(this.buttonStateTimer);
      let added = false;
      let errorMessage = '';

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.remove('is-success');
      submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        errorMessage = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          errorMessage = Object.values(data.errors).flat().join(', ');
        } else if (typeof data.errors === 'string') {
          errorMessage = data.errors;
        }

        if (!response.ok || data.status) throw new Error(errorMessage || theme.strings.cartError);
        added = true;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 300); // D1 2026-09: vorher 700 ms
        }

        // A failed follow-up read does not turn a confirmed add into a failed purchase action.
        const newCartData = await fetch(`${theme.routes.cart}.js`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);
        // add.js returns the resulting line quantity. A parallel cart GET was not a reliable
        // before-snapshot and cost an extra request on every add.
        const addedQuantity = Number(formData.get('quantity')) || 1;
        const oldQuantity = Math.max(0, Number(data.quantity) - addedQuantity);

        // Check if product was already in the cart
        if (oldQuantity > 0) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity,
              newQuantity: data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (!added) {
          const message = errorMessage || theme.strings.cartError;
          this.setErrorMsgState(message);
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: message }
          }));
        }
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitting = false;
        submitBtn.removeAttribute('aria-disabled');
        if (added) {
          submitBtn.classList.add('is-success');
          this.buttonStateTimer = setTimeout(() => {
            submitBtn.classList.remove('is-loading', 'is-success');
          }, 1400);
        } else {
          submitBtn.classList.remove('is-loading', 'is-success');
        }
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response.sections && response.sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
